import React from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
} from "@chakra-ui/react";

const styles = {
    container: {
        fontFamily: "'Arial', sans-serif",
        lineHeight: "1.6",
        color: "#333",
    },
    title: {
        textAlign: "center",
        marginBottom: "20px",
        fontSize: "1.8rem",
        fontWeight: "bold",
        color: "#1a202c",
    },
    section: {
        marginBottom: "20px",
    },
    sectionTitle: {
        fontSize: "1.5rem",
        marginBottom: "10px",
        fontWeight: "bold",
        color: "#2d3748",
    },
    subSectionTitle: {
      fontSize: "1.3rem",
      marginBottom: "8px",
      fontWeight: "bold",
      color: "#2c5282",
  },
    paragraph: {
        marginBottom: "10px",
    },
    list: {
        paddingLeft: "20px",
        marginBottom: "10px",
    },
    listItem: {
        marginBottom: "5px",
    },
};

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Privacy Policy</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <div style={styles.container}>
                        <h1 style={styles.title}>BeatStake Privacy Policy</h1>

                        {/* Introduction */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>Introduction</h2>
                            <p style={styles.paragraph}>
                                Welcome to BeatStake! At BeatStake, we value your privacy and are committed to protecting
                                the personal information you share with us. This Privacy Policy explains how we collect, use,
                                and protect your information when you interact with our platform, including when you register
                                as a fan, artist, or third-party user, subscribe to our services, or engage in transactions
                                involving digital products.
                            </p>
                            <p style={styles.paragraph}>
                                Our goal is to provide a secure and trustworthy environment for all our users. To achieve this,
                                we adhere to applicable data protection laws and work with trusted third-party services like
                                Stripe for identity verification and payment processing. It’s important to note that certain
                                data handled by Stripe is covered by Stripe’s Privacy Policy, which we encourage you to review.
                            </p>
                            <p style={styles.paragraph}>
                                This Privacy Policy applies to all users of BeatStake and governs how your data is managed
                                when you access or use our website, mobile applications, and related services. By using
                                BeatStake, you agree to the terms outlined in this policy. If you have any questions or concerns,
                                please feel free to contact us using the details provided below.
                            </p>
                            <p style={styles.paragraph}>
                                Your privacy is our priority, and we’re dedicated to ensuring your privacy remains safeguarded
                                at every step of your BeatStake journey.
                            </p>
                        </section>

                        {/* Definitions */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>1. Definitions</h2>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>
                                    <strong>Platform:</strong> Refers to the BeatStake website, mobile applications, and all
                                    related services provided by BeatStake.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>User:</strong> Any individual who accesses, interacts with, or registers on the
                                    platform, including fans, artists, and third-party users.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Fans:</strong> Users who browse or purchase shares in digital products on the platform.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Artists:</strong> Creators who list and sell shares of their digital products on the
                                    platform.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Third Parties:</strong> Users, other than artists, who list and sell shares of digital
                                    products on the platform.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Personal Information:</strong> Any information that can be used to identify an
                                    individual, either directly or indirectly, including but not limited to name, email address,
                                    payment details, and identification documents.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Cookies:</strong> Small files stored on a user’s device that collect information about
                                    their use of the platform.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Stripe:</strong> The third-party service provider used by BeatStake for identity
                                    verification and payment processing.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Privacy Policy:</strong> This document, which outlines how BeatStake collects, uses,
                                    shares, and protects user information.
                                </li>
                            </ul>
                        </section>

                        {/* Legal Basis for Processing Data */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>2. Legal Basis for Processing Data</h2>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>
                                    <strong>Consent:</strong> We collect and process user data with explicit consent. This consent
                                    is obtained when users agree to our policies, register for an account, subscribe to services,
                                    or participate in specific platform activities. Users have the right to withdraw consent at
                                    any time without affecting the legality of processing based on prior consent.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Contractual Necessity:</strong> Processing user data is essential to fulfilling our
                                    contractual obligations. This includes managing user accounts, facilitating transactions,
                                    verifying ownership of digital assets, and providing the services users have requested.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Legitimate Interests:</strong> We process user data to enhance platform functionality,
                                    improve user experience, ensure the security of our systems, and prevent fraudulent
                                    activities. These legitimate interests are balanced against user rights and expectations to
                                    ensure data is handled appropriately.
                                </li>
                                <li style={styles.listItem}>
                                    <strong>Legal Obligations:</strong> Certain data is processed to comply with legal and
                                    regulatory requirements. This includes obligations related to fraud prevention, financial
                                    record-keeping, responding to lawful requests from authorities, and adhering to tax and
                                    anti-money laundering laws.
                                </li>
                            </ul>
                            <p style={styles.paragraph}>
                                By aligning our data processing activities with these legal bases, we ensure transparency,
                                accountability, and compliance with applicable regulations. Users can contact us if they have
                                questions about how their data is processed.
                            </p>
                        </section>

                      

                        {/* 3. Information We Collect */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>3. Information We Collect</h2>
                            <p style={styles.paragraph}>
                                To provide a seamless and secure experience on BeatStake, we collect various types
                                of information when you interact with our platform. This includes information you
                                provide directly, data collected automatically, and information from third-party
                                services. Below is a detailed breakdown of what we collect, why we collect it, and
                                how it is used.
                            </p>

                            {/* Subsection: A. Information You Provide Directly */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>A. Information You Provide Directly</h3>
                                <p style={styles.paragraph}>
                                    When you register for an account as a fan, artist, or third-party user, we collect
                                    personal details such as your full name, email address, username, password, and
                                    contact information. For artists and third-party users, we also require identity
                                    verification via Stripe Identity. This involves photographing a government-issued ID,
                                    such as a state ID, driver’s license, or passport, and capturing a live photograph of
                                    your face using your device’s camera. While Stripe processes this data, BeatStake
                                    relies on verification results provided by Stripe and does not store or access this
                                    sensitive information.
                                </p>
                                <p style={styles.paragraph}>
                                    For payment purposes, we collect billing details and payment method information,
                                    which are securely processed through Stripe. Artists and third-party users are also
                                    required to submit content and documentation to prove ownership of the digital
                                    assets they intend to sell. This may include digital files, certificates, licenses, or
                                    other supporting materials. Additionally, any messages or inquiries you send
                                    through the platform, including feedback and reviews, are collected to improve our
                                    services and facilitate user interactions.
                                </p>
                            </section>

                            {/* Subsection: B. Information We Collect Automatically */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>B. Information We Collect Automatically</h3>
                                <p style={styles.paragraph}>
                                    When you use BeatStake, we automatically collect device and usage data to
                                    enhance platform functionality and security. This includes information such as your
                                    IP address, browser type and version, operating system, and device type. We also
                                    collect data about your platform activity, such as the pages you visit, links you click,
                                    and the duration of your sessions.
                                </p>
                                <p style={styles.paragraph}>
                                    We use cookies, web beacons, and similar tracking technologies to collect
                                    additional usage data, such as your session preferences and navigation patterns.
                                    This helps us personalize your experience and analyze platform performance. You
                                    can manage your cookie preferences through your browser settings or platform
                                    options.
                                </p>
                            </section>

                            {/* Subsection: C. Information From Third Parties */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>C. Information From Third Parties</h3>
                                <p style={styles.paragraph}>
                                    BeatStake relies on third-party services, such as Stripe, to facilitate certain platform
                                    functions. Stripe processes identity verification data, including government-issued
                                    ID images and facial photographs, and handles payment transactions. While
                                    BeatStake does not store this sensitive data, we receive verification results and
                                    payment confirmations to manage user accounts and subscriptions.
                                </p>
                                <p style={styles.paragraph}>
                                    If you link your social media accounts or external services to BeatStake, we may
                                    collect publicly available information, such as your profile name, profile picture, and
                                    interactions with our platform through those services.
                                </p>
                            </section>

                            {/* Subsection: D. Why We Collect This Information */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>D. Why We Collect This Information</h3>
                                <p style={styles.paragraph}>
                                    The information we collect is essential for creating and managing user accounts,
                                    processing payments, and verifying the ownership of digital assets. It also enables
                                    us to personalize your experience, enhance platform security, and comply with legal
                                    obligations such as fraud prevention and regulatory requirements.
                                </p>
                            </section>

                            {/* Subsection: E. Sensitive Information */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>E. Sensitive Information</h3>
                                <p style={styles.paragraph}>
                                    BeatStake does not intentionally collect sensitive personal data, such as health
                                    information, unless required for legal or operational purposes. Users are advised
                                    not to submit such information unless explicitly requested.
                                </p>
                            </section>

                            {/* Subsection: F. Children's Information */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>F. Children's Information</h3>
                                <p style={styles.paragraph}>
                                    BeatStake is not intended for users under the age of 18. We do not knowingly collect
                                    personal information from minors. If we discover that a minor has provided us with
                                    personal data, we will take appropriate steps to delete it promptly.
                                </p>
                            </section>
                        </section>

                 

                        {/* 4. User Consent */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>4. User Consent</h2>
                            <p style={styles.paragraph}>
                                At BeatStake, obtaining, documenting, and respecting user consent is fundamental
                                to our data processing practices, ensuring transparency and compliance with
                                applicable laws.
                            </p>

                            {/* Subsection: Obtaining Consent */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>Obtaining Consent</h3>
                                <p style={styles.paragraph}>
                                    Users provide consent through explicit actions that
                                    demonstrate their agreement to our policies and services. This includes registering
                                    for an account, subscribing to platform services or specific features, and accepting
                                    our Terms of Service, Privacy Policy, or other platform policies. Consent is actively
                                    obtained during key processes through mechanisms such as ticking checkboxes,
                                    submitting forms, or confirming agreements. These steps ensure users are fully
                                    aware of the terms to which they are consenting.
                                </p>
                            </section>

                            {/* Subsection: Documenting Consent */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>Documenting Consent</h3>
                                <p style={styles.paragraph}>
                                    To maintain accountability and transparency, all actions
                                    indicating user consent are securely logged and stored. These records include
                                    timestamps and relevant details that verify when and how consent was obtained.
                                    Consent logs are maintained in compliance with applicable data retention
                                    regulations, enabling us to audit and demonstrate adherence to legal standards
                                    when required.
                                </p>
                            </section>

                            {/* Subsection: Withdrawing Consent */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>Withdrawing Consent</h3>
                                <p style={styles.paragraph}>
                                    Users retain the right to withdraw their consent at any time.
                                    This can be done by updating preferences in their account settings or by contacting
                                    BeatStake support via email or our support portal. Once consent is withdrawn, data
                                    processing based on that consent will cease unless there are other legal bases that
                                    require the continued processing of specific data, such as contractual or legal
                                    obligations.
                                </p>
                            </section>
                        </section>

                        {/* 5. How We Use Your Information */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>5. How We Use Your Information</h2>
                            <p style={styles.paragraph}>
                                At BeatStake, we use the information we collect to deliver a seamless, secure, and
                                personalized experience for all users. Below is a detailed explanation of how your
                                information is utilized:
                            </p>

                            {/* Subsection: A. Providing Platform Services */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>A. Providing Platform Services</h3>
                                <p style={styles.paragraph}>
                                    We use your information to facilitate core platform functions, such as creating and
                                    managing user accounts, processing subscription payments, and enabling
                                    transactions for buying and selling shares in digital products. This ensures the
                                    platform operates smoothly and efficiently for fans, artists, and third-party users.
                                </p>
                            </section>

                            {/* Subsection: B. Verifying User Identities */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>B. Verifying User Identities and Ensuring Security</h3>
                                <p style={styles.paragraph}>
                                    Your information is essential for verifying user identities, particularly for artists and
                                    third-party users. This verification process, conducted through Stripe Identity,
                                    helps maintain a secure environment by preventing fraudulent activity and ensuring
                                    that only authorized users can list and sell digital assets.
                                </p>
                            </section>

                            {/* Subsection: C. Enhancing User Experience */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>C. Enhancing User Experience</h3>
                                <p style={styles.paragraph}>
                                    We analyze usage data to improve platform functionality and deliver personalized
                                    experiences. This includes tailoring content, recommending relevant features, and
                                    optimizing platform performance to meet user needs effectively.
                                </p>
                            </section>

                            {/* Subsection: D. Communicating Updates and Promotions */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>D. Communicating Updates and Promotions</h3>
                                <p style={styles.paragraph}>
                                    Your contact information allows us to keep you informed about important updates,
                                    changes to our policies, and promotional opportunities. These communications
                                    ensure you stay updated on developments relevant to your use of BeatStake.
                                </p>
                            </section>

                            {/* Subsection: E. Complying with Legal Obligations */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>E. Complying with Legal and Regulatory Obligations</h3>
                                <p style={styles.paragraph}>
                                    We use your information to fulfill our legal and regulatory responsibilities, such as
                                    fraud detection, compliance with financial regulations, and responding to lawful
                                    requests from authorities.
                                </p>
                            </section>
                        </section>


{/* 6. How We Share Your Information */}
<section style={styles.section}>
    <h2 style={styles.sectionTitle}>6. How We Share Your Information</h2>
    <p style={styles.paragraph}>
        At BeatStake, we respect your privacy and only share your information in ways
        necessary to provide our services, comply with legal requirements, and improve our
        platform. Below is an explanation of how and when your information may be
        shared:
    </p>

    {/* Subsection: A. Third-Party Services */}
    <section style={styles.section}>
        <h3 style={styles.subSectionTitle}>A. Third-Party Services</h3>
        <p style={styles.paragraph}>
            We collaborate with trusted third-party services to facilitate essential platform functions:
        </p>
        <ul style={styles.list}>
            <li style={styles.listItem}>
                <strong>Stripe Identity Verification and Payment Processing:</strong> For artists
                and third-party users, identity verification is conducted through Stripe, which processes
                data such as government-issued ID photographs and live facial images. Stripe also manages
                payment transactions, including subscription payments. The handling of this data is governed
                by Stripe's Privacy Policy.
            </li>
        </ul>
    </section>

    {/* Subsection: B. Legal Requirements */}
    <section style={styles.section}>
        <h3 style={styles.subSectionTitle}>B. Legal Requirements</h3>
        <p style={styles.paragraph}>
            We may disclose your information to law enforcement, regulatory authorities, or other
            governmental entities when required by law. This includes situations where we need to comply
            with a legal obligation, enforce our Terms of Service, or protect the rights, property, or safety
            of BeatStake, our users, or the public.
        </p>
    </section>

    {/* Subsection: C. Business Operations */}
    <section style={styles.section}>
        <h3 style={styles.subSectionTitle}>C. Business Operations</h3>
        <p style={styles.paragraph}>
            To ensure the smooth operation of our platform, we may share your information with third-party
            service providers. These providers assist with:
        </p>
        <ul style={styles.list}>
            <li style={styles.listItem}>
                <strong>Hosting Services:</strong> Storing and maintaining platform data securely.
            </li>
            <li style={styles.listItem}>
                <strong>Analytics Services:</strong> Analyzing user activity to improve platform performance
                and features.
            </li>
            <li style={styles.listItem}>
                <strong>Customer Support:</strong> Managing inquiries and enhancing user experience.
            </li>
        </ul>
        <p style={styles.paragraph}>
            These service providers are contractually obligated to handle your data securely
            and only for purposes necessary to provide their services to us.
        </p>
    </section>

    {/* Subsection: D. Aggregated or Anonymized Data */}
    <section style={styles.section}>
        <h3 style={styles.subSectionTitle}>D. Aggregated or Anonymized Data</h3>
        <p style={styles.paragraph}>
            We may use aggregated or anonymized data, which cannot be used to identify you, for
            purposes such as marketing, research, and analytics. For example, we might analyze platform
            usage trends to enhance features or share generalized insights with stakeholders.
        </p>
    </section>
</section>


                        {/* 7. Data Retention */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>7. Data Retention</h2>
                            <p style={styles.paragraph}>
                                At BeatStake, we retain your information only as long as necessary to fulfill the purposes outlined
                                in this Privacy Policy or to comply with legal, regulatory, and operational requirements. Below is
                                an overview of how we manage data retention:
                            </p>

                            {/* Subsection: A. Retention Periods for Different Types of Data */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>A. Retention Periods for Different Types of Data</h3>
                                <ul style={styles.list}>
                                    <li style={styles.listItem}>
                                        <strong>Account Information:</strong> Retained for as long as your account remains active.
                                        Deleted accounts may be retained temporarily for legal or dispute resolution purposes.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>Transactional Records:</strong> Retained for a minimum of seven years to comply
                                        with financial regulations and for audit purposes.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>Identity Verification Data:</strong> Stored by Stripe, not by BeatStake, in accordance
                                        with Stripe’s Privacy Policy.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>Content and Asset Information:</strong> Retained as long as listings are active. Deleted
                                        or inactive listings may be retained temporarily for compliance or dispute resolution.
                                    </li>
                                </ul>
                            </section>

                            {/* Subsection: B. Criteria for Determining Retention Periods */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>B. Criteria for Determining Retention Periods</h3>
                                <ul style={styles.list}>
                                    <li style={styles.listItem}>
                                        <strong>Regulatory Requirements:</strong> Certain data is retained to comply with legal
                                        obligations, such as financial record-keeping.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>Operational Needs:</strong> Retained to resolve disputes, prevent fraud, or address
                                        inquiries.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>User Activity:</strong> Inactive accounts may result in data deletion or anonymization
                                        after prior notice.
                                    </li>
                                </ul>
                                <p style={styles.paragraph}>
                                    Once the retention period expires, data is securely deleted, anonymized, or otherwise disposed
                                    of in accordance with industry standards.
                                </p>
                            </section>
                        </section>

                        {/* 8. Your Rights */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>8. Your Rights</h2>
                            <p style={styles.paragraph}>
                                At BeatStake, we respect your rights regarding your personal information and provide you with
                                the tools and options to exercise them. Below is an overview of your key rights and how to
                                utilize them:
                            </p>

                            {/* Subsection: A. Access and Portability */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>A. Access and Portability</h3>
                                <p style={styles.paragraph}>
                                    You have the right to request access to the personal data BeatStake holds about you, including
                                    how it is used and shared. We can provide a copy of your data in a portable format for transfer
                                    to another service if desired.
                                </p>
                            </section>

                            {/* Subsection: B. Correction and Deletion */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>B. Correction and Deletion</h3>
                                <p style={styles.paragraph}>
                                    You can update or correct inaccuracies in your personal information at any time through your
                                    account settings. Requests for account or specific data deletion can also be made, though
                                    certain data may need to be retained for compliance or legal obligations.
                                </p>
                            </section>

                            {/* Subsection: C. Opt-Out Options */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>C. Opt-Out Options</h3>
                                <ul style={styles.list}>
                                    <li style={styles.listItem}>
                                        <strong>Cookies and Tracking:</strong> Manage preferences through your browser settings or
                                        the platform’s cookie management tools.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>Marketing Communications:</strong> Opt out of promotional emails or notifications
                                        by following unsubscribe instructions or updating account preferences.
                                    </li>
                                </ul>
                            </section>

                            {/* Subsection: D. Jurisdiction-Specific Rights */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>D. Jurisdiction-Specific Rights</h3>
                                <ul style={styles.list}>
                                    <li style={styles.listItem}>
                                        <strong>California Consumer Privacy Act (CCPA):</strong> California residents can opt out of
                                        data sale, request data deletion, and request details of data usage and sharing.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>General Data Protection Regulation (GDPR):</strong> EU residents have rights to
                                        restrict processing, object to data usage, and lodge complaints with a data protection
                                        authority.
                                    </li>
                                </ul>
                            </section>

                            {/* Subsection: E. How to Exercise Your Rights */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>E. How to Exercise Your Rights</h3>
                                <p style={styles.paragraph}>
                                    To exercise any of your rights, please contact us. We will respond promptly in compliance with
                                    applicable laws. Verification of identity may be required for certain requests.
                                </p>
                            </section>
                        </section>


{/* 9. International Data Transfers */}
<section style={styles.section}>
    <h2 style={styles.sectionTitle}>9. International Data Transfers</h2>
    <p style={styles.paragraph}>
        At BeatStake, we prioritize the protection of user data by ensuring it is processed
        and stored in compliance with applicable data protection laws. Our approach to
        data transfers is structured to maintain transparency and safeguard user
        information.
    </p>
    <section style={styles.section}>
        <h3 style={styles.subSectionTitle}>Current Practices</h3>
        <p style={styles.paragraph}>
            Currently, all user data is processed within the user’s country of
            origin. BeatStake does not engage in international data transfers, ensuring that user
            data remains subject to the legal and regulatory frameworks of its country of origin.
        </p>
    </section>
    <section style={styles.section}>
        <h3 style={styles.subSectionTitle}>Future Transfers</h3>
        <p style={styles.paragraph}>
            If the need arises to transfer data internationally, BeatStake will
            ensure that such transfers comply with the data protection laws of both the country
            of origin and the destination. Necessary safeguards, such as standard contractual
            clauses or equivalent protective measures, will be implemented to protect user
            data during international transfers. Users will be informed of any significant changes
            to these practices to ensure continued transparency.
        </p>
    </section>
</section>

{/* 10. Data Protection Officer (DPO) */}
<section style={styles.section}>
    <h2 style={styles.sectionTitle}>10. Data Protection Officer (DPO)</h2>
    <p style={styles.paragraph}>
        BeatStake is committed to maintaining high standards of data protection and
        privacy compliance. The appointment of a Data Protection Officer (DPO) will be
        assessed based on the platform’s needs and regulatory requirements.
    </p>
    <section style={styles.section}>
        <h3 style={styles.subSectionTitle}>Current Status</h3>
        <p style={styles.paragraph}>
            At present, BeatStake has not appointed a DPO. Our current data
            protection practices are managed internally by our compliance and support teams
            to ensure adherence to applicable data protection laws and standards.
        </p>
    </section>
    <section style={styles.section}>
        <h3 style={styles.subSectionTitle}>Future Plans</h3>
        <p style={styles.paragraph}>
            As BeatStake continues to grow and expand, we will evaluate the
            necessity of appointing a DPO in alignment with evolving business operations and
            legal requirements. Should the need for a DPO arise, we will ensure that the
            appointed individual is suitably qualified to oversee data protection measures and
            address user concerns effectively.
        </p>
        <p style={styles.paragraph}>
            Users with questions about data protection can contact our support team for assistance.
        </p>
    </section>
</section>

{/* 11. Data Breach Notification */}
<section style={styles.section}>
    <h2 style={styles.sectionTitle}>11. Data Breach Notification</h2>
    <p style={styles.paragraph}>
        At BeatStake, the security of user data is a top priority. We have implemented
        measures to promptly detect and respond to data breaches, ensuring compliance
        with applicable legal requirements and transparency with our users.
    </p>
    <section style={styles.section}>
        <h3 style={styles.subSectionTitle}>Detection and Response</h3>
        <p style={styles.paragraph}>
            BeatStake employs advanced monitoring tools and
            security protocols to identify potential data breaches. Our dedicated team works
            swiftly to assess the scope and impact of any breach, implement mitigation
            measures, and prevent further unauthorized access or harm.
        </p>
    </section>
    <section style={styles.section}>
        <h3 style={styles.subSectionTitle}>User Notification</h3>
        <p style={styles.paragraph}>
            In the event of a data breach involving personal information, BeatStake will notify
            affected users within 72 hours, in accordance with applicable laws. Notifications will
            be sent via email or in-platform messages and will include:
        </p>
        <ul style={styles.list}>
            <li style={styles.listItem}>
                A summary of the breach, including the nature and extent of the incident.
            </li>
            <li style={styles.listItem}>
                Details of the affected data categories and individuals impacted.
            </li>
            <li style={styles.listItem}>
                Steps BeatStake is taking to mitigate the breach and secure data.
            </li>
            <li style={styles.listItem}>
                Recommended actions users can take to protect themselves, such as updating
                passwords or monitoring account activity.
            </li>
        </ul>
    </section>
</section>
 {/* 12. Cookies and Tracking Technologies */}
 <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>12. Cookies and Tracking Technologies</h2>
                            <p style={styles.paragraph}>
                                BeatStake uses cookies and similar tracking technologies to enhance your
                                experience on the platform, analyze usage, and deliver personalized content. This
                                section explains what cookies are, how we use them, and how you can manage your
                                preferences.
                            </p>

                            {/* Subsection: A. What Are Cookies? */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>A. What Are Cookies?</h3>
                                <p style={styles.paragraph}>
                                    Cookies are small text files stored on your device when you
                                    visit a website. They help websites function effectively, remember your preferences,
                                    and provide insights into user activity to improve services.
                                </p>
                            </section>

                            {/* Subsection: B. Types of Cookies We Use */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>B. Types of Cookies We Use</h3>
                                <ul style={styles.list}>
                                    <li style={styles.listItem}>
                                        <strong>Essential Cookies:</strong> Necessary for the platform to function
                                        correctly, enabling core features like user login, secure payment processing, and account settings.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>Analytics Cookies:</strong> Provide information on platform usage, such as
                                        page visits and navigation patterns, to improve performance and user experience.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>Marketing Cookies:</strong> Display personalized content and advertisements based on user interactions, and track the effectiveness of promotional campaigns.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>Preference Cookies:</strong> Remember user choices, such as language preferences or account settings, to provide a customized experience.
                                    </li>
                                </ul>
                            </section>

                            {/* Subsection: C. How We Use Cookies */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>C. How We Use Cookies</h3>
                                <p style={styles.paragraph}>
                                    Cookies are used to ensure platform functionality and security, analyze user behavior
                                    to improve features, deliver personalized content, and facilitate secure transactions
                                    and account management.
                                </p>
                            </section>

                            {/* Subsection: D. Managing Your Cookie Preferences */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>D. Managing Your Cookie Preferences</h3>
                                <ul style={styles.list}>
                                    <li style={styles.listItem}>
                                        <strong>Browser Settings:</strong> Adjust browser settings to block or delete specific cookies.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>Platform Settings:</strong> Opt in or out of non-essential cookies directly on the platform.
                                    </li>
                                    <li style={styles.listItem}>
                                        <strong>Third-Party Cookies:</strong> Manage preferences using tools or opt-out mechanisms provided by third-party services.
                                    </li>
                                </ul>
                                <p style={styles.paragraph}>
                                    <strong>Important Note:</strong> Disabling certain cookies may limit platform functionality, such as personalized features or secure transactions.
                                </p>
                            </section>
                        </section>

                        {/* 13. Third-Party Links */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>13. Third-Party Links</h2>
                            <p style={styles.paragraph}>
                                BeatStake may contain links to third-party websites, services, or resources that are not
                                owned or controlled by us. These links are provided for your convenience and may include
                                external resources, partner services, or promotional offers.
                            </p>
                            <p style={styles.paragraph}>
                                We emphasize that BeatStake is not responsible for the privacy practices, content, or
                                policies of any third-party websites. Once you leave our platform by clicking on a third-party
                                link, your interactions with those sites are governed by their respective privacy policies and
                                terms of use.
                            </p>
                            <p style={styles.paragraph}>
                                While we strive to work with reputable third parties, BeatStake does not endorse or assume
                                liability for the actions, content, or data handling practices of any third-party website or
                                service linked to our platform.
                            </p>
                        </section>


                        {/* 14. Stripe Identity and Payments */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>14. Stripe Identity and Payments</h2>
                            <p style={styles.paragraph}>
                                At BeatStake, we rely on Stripe as a trusted third-party service provider for both
                                identity verification and payment processing. Stripe plays a critical role in ensuring
                                secure and efficient transactions and user verification on our platform.
                            </p>

                            {/* Subsection: A. Stripe Identity Verification */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>A. Stripe Identity Verification</h3>
                                <p style={styles.paragraph}>
                                    For artists and third-party users, identity verification is a mandatory process conducted
                                    through Stripe Identity. This process involves the collection of:
                                </p>
                                <ul style={styles.list}>
                                    <li style={styles.listItem}>
                                        A photograph of a government-issued ID such as the state ID, passport, or driver’s license.
                                    </li>
                                    <li style={styles.listItem}>
                                        A live photograph of your face taken through your device’s camera.
                                    </li>
                                </ul>
                                <p style={styles.paragraph}>
                                    Stripe uses this data to verify your identity and confirm that you meet our platform’s
                                    requirements. While BeatStake relies on Stripe for verification results, we do not store
                                    or access the personal data submitted for this purpose.
                                </p>
                            </section>

                            {/* Subsection: B. Stripe Payment Processing */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>B. Stripe Payment Processing</h3>
                                <ul style={styles.list}>
                                    <li style={styles.listItem}>
                                        Processing subscription fees for artists and third-party users.
                                    </li>
                                    <li style={styles.listItem}>
                                        Handling payments for shares purchased by fans.
                                    </li>
                                    <li style={styles.listItem}>
                                        Managing payouts to artists and third-party users for completed transactions.
                                    </li>
                                </ul>
                                <p style={styles.paragraph}>
                                    Your payment information, such as credit or debit card details, is securely collected
                                    and processed by Stripe. BeatStake does not store your sensitive payment information,
                                    ensuring your financial data is handled with the highest security standards.
                                </p>
                            </section>

                            {/* Subsection: C. Stripe’s Privacy Policy */}
                            <section style={styles.section}>
                                <h3 style={styles.subSectionTitle}>C. Stripe’s Privacy Policy</h3>
                                <p style={styles.paragraph}>
                                    All data collected and processed by Stripe is governed by{" "}
                                    <a
                                        href="https://stripe.com/privacy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={styles.link}
                                    >
                                        Stripe’s Privacy Policy
                                    </a>
                                    , which outlines how they collect, use, and protect your information. By using BeatStake,
                                    you acknowledge and agree to Stripe’s role in identity verification and payment processing,
                                    as well as to their data handling practices as described in their Privacy Policy.
                                </p>
                            </section>
                        </section>

                        {/* 15. Updates to This Policy */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>15. Updates to This Policy</h2>
                            <p style={styles.paragraph}>
                                We may update this Privacy Policy periodically to reflect changes in our practices,
                                legal requirements, or platform features. Any updates will be communicated
                                through email notifications, platform announcements, or both.
                            </p>
                            <p style={styles.paragraph}>
                                The latest version of this Privacy Policy will always include the effective date at the
                                top of the document. We encourage you to review it regularly to stay informed
                                about how we protect your information.
                            </p>
                        </section>

                        {/* 16. Contact Us */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>16. Contact Us</h2>
                            <p style={styles.paragraph}>
                                If you have any questions, concerns, or requests regarding this Privacy Policy or how
                                your information is handled, please feel free to reach out to us.
                            </p>
                        </section>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default PrivacyPolicyModal;